import { useTVStore, useVODStore } from '@/shared/store'
import {
    selectSetItemDetails,
    selectSetVodRowItemType,
} from '@/shared/store/storeSelectors/vodSelectors'
import { useWebStore } from '@/shared/store'
import { useAnalytics } from '@/app/context/AnalyticsContext/AnalyticsContext'
import { debounce, parseAspectRatioToNumber } from '@/shared/lib'
import {
    cutvType,
    debounceSpeedMedium,
    epgPagePath,
    tvPagePath,
    vodContentType,
    VodPageDetailsType,
    vodPagePath,
    VodPageType,
    webPath,
} from '@/shared/constants'
import { pageType } from '@/shared/store/types'
import useItemDetails from '@/features/VOD/hooks/useItemDetails'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import { openChannelFromUrl } from '@/shared/lib/openChannelFromUrl'
import useChannelChangeHook from '@/components/tv/hooks/useChannelChangeHook'
import { useNavigate } from 'react-router-dom'
import { useTopMenu } from '@/widgets/TopMenu/hooks/useTopMenu'
import { useEffect, useState } from 'react'
import placeholderPoster from '@/shared/assets/images/placeholderPoster.webp'

export const useVodItemViewLogic = (
    item: VodItem,
    handleGoToCategoryButtonClick: (
        itemId: string,
        title: string,
        type: pageType
    ) => void
) => {
    const { details, getItemDetails } = useItemDetails()
    const setItemDetails = useVODStore(selectSetItemDetails)
    const setVodRowItemType = useVODStore(selectSetVodRowItemType)
    const { channelsData } = useTVStore()
    const { handleChannelChange } = useChannelChangeHook()
    const { handleTvClick, handleEpgClick, handleVodClick } = useTopMenu()
    const Analytics = useAnalytics()
    const {
        label,
        assetType,
        productId,
        title,
        imageUrl,
        externalUri,
        duration,
        position,
    } = item
    const navigate = useNavigate()
    const watchingProgress =
        duration && position ? (position / (duration / 1000)) * 100 : 0

    const itemAspectRatio = parseAspectRatioToNumber(item)

    const showDetails = assetType
        ? [...vodContentType, cutvType].includes(assetType)
        : false

    const navigateToCategory = () => {
        if (productId) {
            handleGoToCategoryButtonClick(productId, title, VodPageType)
            Analytics?.analyticsLogEvent('vod_item_click', { productId, title })
            navigate(vodPagePath)
            handleVodClick(false)
        }
    }

    const handleExternalLink = () => {
        if (!externalUri) return

        const channelId = openChannelFromUrl(externalUri, 'livetv', 'contentId')
        const program = openChannelFromUrl(externalUri, 'program')
        const vod = openChannelFromUrl(externalUri, 'vod')
        const http = openChannelFromUrl(externalUri, 'http')

        if (channelId) {
            const channel = channelsData.find(
                (channel) => channel.id === channelId
            )
            if (channel) {
                handleTvClick()
                navigate(tvPagePath)
                handleChannelChange(channel)
            }
        } else if (program && !http) {
            navigate(epgPagePath)
            handleEpgClick()
        } else if (vod && !http) {
            navigate(vodPagePath)
            handleVodClick(true)
        } else {
            window.open(externalUri, 'external link')
        }

        Analytics?.analyticsLogEvent('deeplink_item_click', { to: externalUri })
    }

    const showItemDetails = () => {
        // :QUICKFIX TOYA
        // details jest ładowane asynchronicznie przy evencie mouseenter
        // co nie sprawdza się na urządzeniach mobilnych
        // if (!details?.productId) return
        setItemDetails({
            productId: item.productId,
            trailerId: details?.trailerProductId,
            assetType: item.assetType,
            id: `vod:${item.productId}`,
            offers: details?.offers?.length ? details.offers : [],
        })
        if (productId) {
            handleGoToCategoryButtonClick(productId, title, VodPageDetailsType)
        }
        navigate(vodPagePath)
        handleVodClick(false)
    }

    const setSelectedWeb = useWebStore((state) => state.setSelectedWeb)

    const showWebDetails = () => {
        setSelectedWeb({
            uri: item.externalUri,
            productId: item.productId,
            assetType: 'web',
            id: `web:${item.productId}`,
            customPlayer: item.customPlayer,
            imageUrl: item.imageUrl,
            title: item.title,
        })
        navigate(webPath)
    }

    const handleItemClick = () => {
        if (assetType !== 'external' && assetType !== 'web') {
            setVodRowItemType(assetType || '')
        }

        if (assetType === 'web' && item.customPlayer != undefined) {
            showWebDetails()
            return
        }

        if (assetType === 'cat') {
            navigateToCategory()
            return
        }

        if (externalUri) {
            handleExternalLink()
        } else {
            showItemDetails()
        }
    }

    const handleMouseEnter = debounce(() => {
        if (showDetails) {
            getItemDetails({
                productId: item.productId,
                assetType: item.assetType,
                id: `vod:${item.productId}` || '',
            })
        }
    }, debounceSpeedMedium)

    const handleMouseLeave = () => {
        handleMouseEnter.cancel()
    }

    const [loadedImageSrc, setLoadedImageSrc] =
        useState<string>(placeholderPoster)

    useEffect(() => {
        setLoadedImageSrc(placeholderPoster)
        if (imageUrl) {
            setLoadedImageSrc(imageUrl)
        } else {
            setLoadedImageSrc(placeholderPoster)
        }
    }, [imageUrl])

    return {
        itemAspectRatio,
        handleMouseEnter,
        handleMouseLeave,
        handleItemClick,
        details,
        loadedImageSrc,
        title,
        label,
        watchingProgress,
    }
}
